/** German speech-to-text via the browser's SpeechRecognition — $0, no
 * audio ever reaches our servers (only the recognized TEXT is sent).
 *
 * THE THING THIS GETS WRONG IF YOU LET IT: the browser's recogniser stops
 * on its own. It stops after a result, it stops after a couple of seconds
 * of silence, and on iOS it stops for reasons of its own. A learner who
 * pauses to think — which is most of what a language learner does — comes
 * back to a microphone that is no longer listening, and nothing they say
 * is heard again.
 *
 * So this wrapper keeps it alive: `continuous` where the browser honours
 * it, and an automatic restart whenever the engine ends while the caller
 * still wants to listen. The caller says when to stop; silence does not.
 *
 * A paid-STT upgrade (e.g. Azure Speech) swaps THIS module only — the
 * session API is text-based either way.
 */

export interface Recognizer {
  start(): void;
  stop(): void;
}

export interface RecognizerOptions {
  lang?: string;
  /** One finished utterance — the engine decided the learner stopped. */
  onFinal: (text: string) => void;
  /** The learner is mid-sentence. Used to show that they are being heard,
   *  and to know a pause is a pause and not the end of a turn. */
  onInterim?: (text: string) => void;
  /** Listening really has stopped, because the caller asked it to. NOT
   *  fired for the engine's own stops, which are restarted instead. */
  onEnd?: () => void;
  onError?: (error: unknown) => void;
}

type Recognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult:
    | ((event: {
        results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
      }) => void)
    | null;
  onend: (() => void) | null;
  onerror: ((event: unknown) => void) | null;
  start(): void;
  stop(): void;
  abort?: () => void;
};

type SpeechRecognitionCtor = new () => Recognition;

/** How long to wait before restarting after the engine ends by itself.
 *  Long enough not to spin, short enough that a learner drawing breath
 *  does not notice a gap. */
const RESTART_DELAY_MS = 250;

/** Errors that mean restarting is pointless — the learner has to act. */
const FATAL = new Set(["not-allowed", "service-not-allowed", "audio-capture"]);

function recognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function sttAvailable(): boolean {
  return recognitionCtor() !== null;
}

/** A recogniser that keeps listening until it is told not to. */
export function createRecognizer(opts: RecognizerOptions): Recognizer | null {
  const Ctor = recognitionCtor();
  if (!Ctor) return null;

  const recognition = new Ctor();
  recognition.lang = opts.lang ?? "de-DE";
  // continuous: keep going across pauses rather than ending on the first
  // silence. Safari ignores it, which is why the restart below exists too.
  recognition.continuous = true;
  // interim results are how we can tell "still talking" from "finished".
  recognition.interimResults = true;

  /** The caller wants to be listening. Only `stop()` clears it. */
  let wanted = false;
  /** A restart is already scheduled — do not queue a second. */
  let restarting: ReturnType<typeof setTimeout> | null = null;
  let dead = false;

  const startEngine = () => {
    try {
      recognition.start();
    } catch {
      // Already running. The browser throws rather than no-opping.
    }
  };

  recognition.onresult = (event) => {
    let final = "";
    let interim = "";
    for (let i = 0; i < event.results.length; i += 1) {
      const result = event.results[i];
      const text = result[0]?.transcript ?? "";
      if (result.isFinal) final += text;
      else interim += text;
    }
    if (interim.trim()) opts.onInterim?.(interim.trim());
    if (final.trim()) opts.onFinal(final.trim());
  };

  recognition.onend = () => {
    // The ENGINE stopping is not the learner stopping. If we still want to
    // be listening, start it again — this is the whole point of the module.
    if (!wanted || dead) {
      opts.onEnd?.();
      return;
    }
    if (restarting !== null) return;
    restarting = setTimeout(() => {
      restarting = null;
      if (wanted && !dead) startEngine();
    }, RESTART_DELAY_MS);
  };

  recognition.onerror = (event) => {
    const code = (event as { error?: string })?.error;
    if (code === "no-speech" || code === "aborted") {
      // Silence is not a failure. `onend` follows and restarts.
      return;
    }
    if (code && FATAL.has(code)) {
      // Permission refused or no microphone: restarting would loop forever.
      dead = true;
      wanted = false;
    }
    opts.onError?.(event);
  };

  return {
    start: () => {
      if (dead) return;
      wanted = true;
      startEngine();
    },
    stop: () => {
      wanted = false;
      if (restarting !== null) {
        clearTimeout(restarting);
        restarting = null;
      }
      try {
        recognition.stop();
      } catch {
        // Not running. Nothing to stop.
      }
    },
  };
}
